import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import {
    Mail,
    Handshake,
    Wine,
    Globe,
    TrendingUp,
    ArrowRight,
    Clock,
    MapPin,
} from "lucide-react";

const CONTACT_EMAIL = "bizmarketapp@gmail.com";

const CATEGORIES = [
    {
        id: "general",
        icon: Mail,
        title: "General Inquiries",
        body: "Questions about CellarGlobal, our platform, or how we work.",
        subject: "General Inquiry",
    },
    {
        id: "partnership",
        icon: Handshake,
        title: "Partnership Opportunities",
        body: "Strategic partnerships, distribution alliances, industry collaborations.",
        subject: "Partnership Opportunity",
    },
    {
        id: "winery",
        icon: Wine,
        title: "Winery Applications",
        body: "Wineries interested in joining our curated international platform.",
        subject: "Winery Application",
    },
    {
        id: "importer",
        icon: Globe,
        title: "Importer Requests",
        body: "Importers, distributors and buyers seeking access to selected wineries.",
        subject: "Importer Request",
    },
    {
        id: "business",
        icon: TrendingUp,
        title: "Business Development",
        body: "Investment, growth, and commercial development conversations.",
        subject: "Business Development",
    },
];

const DEFAULT_CATEGORY = CATEGORIES[0];

export default function Contact() {
    const [category, setCategory] = useState(DEFAULT_CATEGORY.id);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [company, setCompany] = useState("");
    const [country, setCountry] = useState("");
    const [message, setMessage] = useState("");

    const selected = CATEGORIES.find((c) => c.id === category) || DEFAULT_CATEGORY;

    const handleSubmit = (e) => {
        e.preventDefault();
        const subject = `[CellarGlobal · ${selected.subject}] ${name || "New enquiry"}`;
        const bodyLines = [
            `Name: ${name}`,
            `Email: ${email}`,
            company ? `Company: ${company}` : null,
            country ? `Country: ${country}` : null,
            `Category: ${selected.title}`,
            "",
            "Message:",
            message,
        ].filter(Boolean).join("\n");
        const href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines)}`;
        window.location.href = href;
    };

    return (
        <Layout transparentNav>
            {/* HERO */}
            <section className="relative pt-40 pb-24 md:pt-56 md:pb-32 px-6 md:px-12 lg:px-24 border-b border-[#27272A]" data-testid="contact-hero">
                <div className="max-w-4xl">
                    <p className="overline about-fade-up">Contact</p>
                    <h1 className="font-display text-5xl md:text-7xl lg:text-[5.5rem] mt-6 leading-[0.95] about-fade-up" style={{ animationDelay: "120ms" }}>
                        Get in Touch with
                        <em className="font-display-italic text-[#C9A961]"> CellarGlobal.</em>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-[#A1A1AA] max-w-2xl leading-relaxed about-fade-up" style={{ animationDelay: "240ms" }}>
                        Whether you&apos;re a winery, an importer, or a strategic partner — we&apos;d love to hear from you.
                        Choose the topic that best describes your enquiry.
                    </p>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-b border-[#27272A]" data-testid="contact-categories">
                <div className="max-w-3xl mb-16">
                    <p className="overline">Choose a topic</p>
                    <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">How can we help?</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {CATEGORIES.map(({ id, icon: Icon, title, body }) => {
                        const active = category === id;
                        return (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setCategory(id)}
                                data-testid={`contact-category-${id}`}
                                aria-pressed={active}
                                className={`text-left border p-6 md:p-8 transition-all duration-500 mkt-card-lift ${
                                    active
                                        ? "border-[#C9A961] bg-[#0A0A0A]"
                                        : "border-[#27272A] hover:border-[#3F3F46]"
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <Icon className={`w-6 h-6 ${active ? "text-[#C9A961]" : "text-[#A1A1AA]"}`} strokeWidth={1.25} />
                                    {active && (
                                        <span className="text-[10px] tracking-[0.35em] uppercase text-[#C9A961]">Selected</span>
                                    )}
                                </div>
                                <h3 className={`font-display text-2xl mt-6 ${active ? "text-white" : "text-[#E4E4E7]"}`}>{title}</h3>
                                <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">{body}</p>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* FORM */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-b border-[#27272A]" data-testid="contact-form-section">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <p className="overline">Send a Message</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
                            {selected.title}.
                        </h2>
                        <p className="mt-6 text-base text-[#A1A1AA] leading-relaxed max-w-md">
                            Fill in the form and your default email client will open with your enquiry ready to send to our team.
                        </p>
                        <div className="mt-10 space-y-4 text-sm text-[#A1A1AA]">
                            <div className="flex items-start gap-3">
                                <Clock className="w-4 h-4 text-[#C9A961] mt-1 shrink-0" strokeWidth={1.5} />
                                <span>Response time typically within <span className="text-[#E4E4E7]">48 business hours</span>.</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-[#C9A961] mt-1 shrink-0" strokeWidth={1.5} />
                                <span>Mendoza, Argentina · Serving importers worldwide.</span>
                            </div>
                            <div className="flex items-start gap-3">
                                <Mail className="w-4 h-4 text-[#C9A961] mt-1 shrink-0" strokeWidth={1.5} />
                                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-[#C9A961] transition-colors duration-500" data-testid="contact-direct-email">
                                    {CONTACT_EMAIL}
                                </a>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="md:col-span-7 space-y-8" data-testid="contact-form">
                        <div className="grid sm:grid-cols-2 gap-8">
                            <Field label="Full Name" required>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="input-line"
                                    placeholder="Jane Cellier"
                                    data-testid="contact-input-name"
                                />
                            </Field>
                            <Field label="Email" required>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-line"
                                    placeholder="you@company.com"
                                    data-testid="contact-input-email"
                                />
                            </Field>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-8">
                            <Field label="Company / Winery">
                                <input
                                    type="text"
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                    className="input-line"
                                    placeholder="Bodega o empresa"
                                    data-testid="contact-input-company"
                                />
                            </Field>
                            <Field label="Country">
                                <input
                                    type="text"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    className="input-line"
                                    placeholder="Argentina, USA, UK…"
                                    data-testid="contact-input-country"
                                />
                            </Field>
                        </div>

                        <Field label="Message" required>
                            <textarea
                                required
                                rows={6}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="input-line resize-none py-3"
                                placeholder="Tell us briefly about your enquiry…"
                                data-testid="contact-input-message"
                            />
                        </Field>

                        <div className="flex flex-wrap items-center gap-6 pt-2">
                            <button type="submit" className="btn-gold hero-cta-glow" data-testid="contact-submit">
                                Send Enquiry <ArrowRight className="w-4 h-4" />
                            </button>
                            <p className="text-xs text-[#71717A] tracking-wide">
                                Opens your email client. Nothing is stored on our servers.
                            </p>
                        </div>
                    </form>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center" data-testid="contact-cta">
                <p className="overline">Prefer to explore first?</p>
                <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight max-w-2xl mx-auto">
                    Discover the wineries we already represent.
                </h2>
                <div className="flex flex-wrap justify-center gap-4 mt-10">
                    <Link to="/wineries" className="btn-gold hero-cta-glow" data-testid="contact-cta-wineries">
                        Explore Wineries <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/about" className="btn-outline" data-testid="contact-cta-about">
                        About CellarGlobal
                    </Link>
                </div>
            </section>
        </Layout>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="overline block mb-3">
                {label}
                {required && <span className="text-[#C9A961] ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}
